/**
 * React Router configuration
 * Defines all routes matching the Next.js pages structure
 */
import { createBrowserRouter, Navigate } from 'react-router-dom';
import MyApp from './pages/_app';

// Lazy load page components for code splitting
import { lazy } from 'react';
import { SUPPORTED_LOCALES } from './lib/i18n/i18n';
import { withLayout } from './lib/vite-adapters/with-layout';
import { LocaleRedirect } from './locale-redirect';

// ============================================================================
// AUTHENTICATION PAGES (public)
// ============================================================================
const SignInPage = lazy(() => import('./pages/sign-in/[[...index]]').then(withLayout));

// ============================================================================
// PUBLIC PAGES
// ============================================================================
const NotFoundPage = lazy(() => import('./pages/404').then(withLayout));

// ============================================================================
// PROTECTED PAGES (require authentication)
// ============================================================================
const HomePage = lazy(() => import('./pages/home/index').then(withLayout));

// Customer pages
const CustomersListPage = lazy(() => import('./pages/customers/index').then(withLayout));
const CustomerDetailPage = lazy(() => import('./pages/customers/[customer_id]/index').then(withLayout));
const CustomerActivityPage = lazy(() => import('./pages/customers/[customer_id]/activity').then(withLayout));
const CustomerInsightsPage = lazy(() => import('./pages/customers/[customer_id]/insights').then(withLayout));
const CustomerInfoPage = lazy(() => import('./pages/customers/[customer_id]/info').then(withLayout));

// Sales Drafts
const DraftsListPage = lazy(() => import('./pages/drafts/index').then(withLayout));
const DraftHeaderPage = lazy(() => import('./pages/drafts/[draft_id]/header').then(withLayout));
const DraftLineItemsPage = lazy(() => import('./pages/drafts/[draft_id]/line-items').then(withLayout));

// Quotes
const QuotesListPage = lazy(() => import('./pages/quotes/index').then(withLayout));
const QuoteDetailPage = lazy(() => import('./pages/quotes/[quote_id]/index').then(withLayout));

// Orders
const OrdersListPage = lazy(() => import('./pages/orders/index').then(withLayout));
const OrderDetailPage = lazy(() => import('./pages/orders/[order_id]/index').then(withLayout));

// Admin pages
const AdminDashboardPage = lazy(() => import('./pages/admin/index').then(withLayout));
const AdminUsersPage = lazy(() => import('./pages/admin/admin-users').then(withLayout));
const AdminCustomersListPage = lazy(() => import('./pages/admin/customers/index').then(withLayout));
const AdminCustomerDetailPage = lazy(() =>
    import('./pages/admin/customers/[customer_id]/index').then(withLayout),
);
const AdminCustomerDetailsPage = lazy(() =>
    import('./pages/admin/customers/[customer_id]/details').then(withLayout),
);

// Other feature pages
const TasksPage = lazy(() => import('./pages/tasks/index').then(withLayout));
const EventsPage = lazy(() => import('./pages/events/index').then(withLayout));
const InventoryPage = lazy(() => import('./pages/inventory/[product_id]').then(withLayout));
const TapiIntegrationPage = lazy(() => import('./pages/integrations/tapi/[phone]').then(withLayout));
const ExplorerPage = lazy(() => import('./pages/explorer').then(withLayout));

export const router = createBrowserRouter([
    ...SUPPORTED_LOCALES.flatMap((locale) =>
        [`/${locale}/*`, `/${locale}`].map((path) => ({
            path,
            element: <LocaleRedirect targetLocale={locale} />,
        })),
    ),
    // Main app routes
    {
        path: '/',
        element: <MyApp />,
        errorElement: <NotFoundPage />,
        children: [
            // Redirect root to /home
            {
                index: true,
                element: <Navigate to="home" replace />,
            },

            // ================================================================
            // PUBLIC ROUTES
            // ================================================================
            {
                path: 'sign-in/*',
                element: <SignInPage />,
            },

            // ================================================================
            // PROTECTED ROUTES (wrapped in AuthGuard)
            // ================================================================
            {
                path: 'home',
                element: <HomePage />,
            },

            // Customer routes
            {
                path: 'customers',
                children: [
                    {
                        index: true,
                        element: <CustomersListPage />,
                    },
                    {
                        path: ':customer_id',
                        children: [
                            {
                                index: true,
                                element: <CustomerDetailPage />,
                            },
                            {
                                path: 'activity',
                                element: <CustomerActivityPage />,
                            },
                            {
                                path: 'insights',
                                element: <CustomerInsightsPage />,
                            },
                            {
                                path: 'info',
                                element: <CustomerInfoPage />,
                            },
                        ],
                    },
                ],
            },

            // Drafts routes
            {
                path: 'drafts',
                children: [
                    {
                        index: true,
                        element: <DraftsListPage />,
                    },
                    {
                        path: ':draft_id',
                        children: [
                            {
                                path: 'header',
                                element: <DraftHeaderPage />,
                            },
                            {
                                path: 'line-items',
                                element: <DraftLineItemsPage />,
                            },
                        ],
                    },
                ],
            },

            // Quotes routes
            {
                path: 'quotes',
                children: [
                    {
                        index: true,
                        element: <QuotesListPage />,
                    },
                    {
                        path: ':quote_id',
                        element: <QuoteDetailPage />,
                    },
                ],
            },

            // Orders routes
            {
                path: 'orders',
                children: [
                    {
                        index: true,
                        element: <OrdersListPage />,
                    },
                    {
                        path: ':order_id',
                        element: <OrderDetailPage />,
                    },
                ],
            },

            // Admin routes
            {
                path: 'admin',
                children: [
                    {
                        index: true,
                        element: <AdminDashboardPage />,
                    },
                    {
                        path: 'admin-users',
                        element: <AdminUsersPage />,
                    },
                    {
                        path: 'customers',
                        children: [
                            {
                                index: true,
                                element: <AdminCustomersListPage />,
                            },
                            {
                                path: ':customer_id',
                                children: [
                                    {
                                        index: true,
                                        element: <AdminCustomerDetailPage />,
                                    },
                                    {
                                        path: 'details',
                                        element: <AdminCustomerDetailsPage />,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },

            // Other feature routes
            {
                path: 'tasks',
                element: <TasksPage />,
            },
            {
                path: 'events',
                element: <EventsPage />,
            },
            {
                path: 'inventory/:product_id',
                element: <InventoryPage />,
            },
            {
                path: 'integrations/tapi/:phone',
                element: <TapiIntegrationPage />,
            },
            {
                path: 'explorer',
                element: <ExplorerPage />,
            },

            // ================================================================
            // 404 - Catch all
            // ================================================================
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
    },
]);
